import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  CContainer,
  CSpinner,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
} from "@coreui/react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useTranslation } from "react-i18next";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import kk from "date-fns/locale/kk";

import DashboardHeader from "./components/DashboardHeader";
import FilterControls from "./components/FilterControls";
import DeviceGrid from "./components/DeviceGrid";
import SummaryAnalyticsModal from "./components/SummaryAnalyticsModal";
import DeviceModals from "./components/DeviceModals";
import DisconnectionsModal from "./components/DisconnectionsModal";
import "./DashboardPage.css";

dayjs.extend(relativeTime);
registerLocale("ru", ru);
registerLocale("kk", kk);

const formatDuration = (seconds) => {
  if (seconds < 0 || !seconds) seconds = 0;
  const h = Math.floor(seconds / 3600)
    .toString()
    .padStart(2, "0");
  const m = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${h}:${m}:${s}`;
};

const formatMetric = (value, type) => {
  if (type.includes("time")) return formatDuration(value);
  if (type.includes("mass_tons")) return `${(value / 1000).toFixed(1)} т`;
  if (type.includes("float")) return value.toFixed(1);
  if (type.includes("sec")) return `${value.toFixed(1)} сек`;
  if (type.includes("pct")) return `${value.toFixed(1)}%`;
  return value;
};

const INITIAL_METRICS_KEYS = [
  "kpis.OEE",
  "kpis.KIO",
  "kpis.KTG",
  "kpis.KIV",
  "kpis.KIN",
  "useful_time",
  "kpis.m3_per_hour",
  "truck_arrivals_total",
  "cycle_count_total",
  "cycle_count_vehicle",
  "cycle_count_ground",
  "estimated_volume_total",
  "estimated_volume_vehicle",
  "estimated_volume_ground",
  "ai_volume_total",
  "averages.vehicle.total_sec",
  "averages.vehicle.empty_sec",
  "averages.vehicle.full_sec",
  "averages.vehicle.unload_sec",
  "averages.vehicle.delay_sec",
  "durations_by_class_sec.full_bucket",
  "durations_by_class_sec.unloading_vehicle",
  "durations_by_class_sec.unloading_ground",
  "durations_by_class_sec.empty_bucket",
  "durations_by_class_sec.actual_downtime",
  "durations_by_class_sec.no_data",
];

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language ? i18n.language.split("-")[0] : "ru";
  const isStaff = localStorage.getItem("is_staff") === "true";
  const isSuperuser = localStorage.getItem("is_superuser") === "true";
  const canDoMarkup = localStorage.getItem("can_do_markup") === "true";
  const organizationName = localStorage.getItem("organizationName");
  const [exportingMultiExcel, setExportingMultiExcel] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  const [passportModal, setPassportModal] = useState({
    visible: false,
    url: "",
    filename: "",
    loading: false,
  });

  const closePassportModal = () => {
    if (passportModal.url) {
      URL.revokeObjectURL(passportModal.url);
    }
    setPassportModal({ visible: false, url: "", filename: "", loading: false });
  };

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("last_seen_desc");
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectedOrgFilter, setSelectedOrgFilter] = useState(
    localStorage.getItem("dashboardOrgFilter") || ""
  );

  useEffect(() => {
    localStorage.setItem("dashboardOrgFilter", selectedOrgFilter);
  }, [selectedOrgFilter]);

  const [multiModalVisible, setMultiModalVisible] = useState(false);
  const [multiStartDate, setMultiStartDate] = useState(
    dayjs().startOf("day").toDate(),
  );
  const [multiEndDate, setMultiEndDate] = useState(
    dayjs().endOf("day").toDate(),
  );
  const [multiPreset, setMultiPreset] = useState("today");
  const [multiData, setMultiData] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState(INITIAL_METRICS_KEYS);
  const [multiLoading, setMultiLoading] = useState(false);
  const [multiCooldown, setMultiCooldown] = useState(0);
  const [disconnectModalVisible, setDisconnectModalVisible] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [saving, setSaving] = useState(false);

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [newDevice, setNewDevice] = useState({ name: "", bucket_volume: 1.0 });
  const [creating, setCreating] = useState(false);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [deleteConfirmationWord, setDeleteConfirmationWord] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [workZones, setWorkZones] = useState([]);
  const [equipmentSpecs, setEquipmentSpecs] = useState([]);
  const [selectedZoneFilter, setSelectedZoneFilter] = useState("");
  const [showMetricsFilter, setShowMetricsFilter] = useState(false);

  const [selectedTypeFilter, setSelectedTypeFilter] = useState("");

  // --- ЛОГИКА УВЕДОМЛЕНИЙ ОБ ИСТЕКАЮЩЕМ СРОКЕ СЛУЖБЫ ---
  const expiringDevices = useMemo(() => {
    return devices.filter(
      (d) =>
        d.warranty_days_left !== null &&
        d.warranty_days_left !== undefined &&
        d.warranty_days_left <= 30
    );
  }, [devices]);

  const [expiringModalDismissed, setExpiringModalDismissed] = useState(() => {
    return sessionStorage.getItem("expiring_modal_dismissed") === "true";
  });

  const fetchDevicesAndZones = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [resDev, resZones, resSpecs, resTypes] = await Promise.all([
        axios.get("/api/devices/", { headers }),
        axios.get("/api/work-zones/", { headers }),
        axios.get("/api/equipment-specs/", { headers }),
        axios.get("/api/vehicle-types/", { headers }),
      ]);
      setDevices(resDev.data.results || resDev.data || []);
      setWorkZones(resZones.data);
      setEquipmentSpecs(resSpecs.data);
      setVehicleTypes(resTypes.data.vehicle_types || resTypes.data || []);
    } catch (err) {
      toast.error("Ошибка загрузки данных");
    } finally {
      setLoading(false);
    }
  };

  const uniqueOrganizations = useMemo(() => {
    if (!isSuperuser) return [];
    const orgs = devices.map((d) => d.organization_name).filter(Boolean);
    return [...new Set(orgs)].sort();
  }, [devices, isSuperuser]);

  const classIdToMeta = useMemo(
    () => ({
      0: { label: t("classes.full_bucket"), color: "#04be61" },
      1: { label: t("classes.unload_vehicle"), color: "#8080ff" },
      2: { label: t("classes.unload_ground"), color: "#ffdd00" },
      3: { label: t("classes.empty_bucket"), color: "#ff3366" },
      4: { label: t("classes.not_visible"), color: "#343a40" },
      10: { label: t("classes.down_full"), color: "#1b5e20" },
      11: { label: t("classes.down_vehicle"), color: "#283593" },
      12: { label: t("classes.down_ground"), color: "#e65100" },
      13: { label: t("classes.down_empty"), color: "#b71c1c" },
      "-1": { label: t("classes.no_data"), color: "#546e7a" },
      "-2": { label: "ТО / Выходной", color: "#7b1fa2" },
      "-3": { label: "Перерыв", color: "#00acc1" },
      default: { label: t("classes.unknown"), color: "#455a64" },
    }),
    [t],
  );

  const COMPARISON_METRICS = useMemo(
    () => [
      {
        key: "kpis.OEE",
        label: t("analysis.kpi_oee", "ОЕЕ %"),
        type: "pct",
        color: "#6610f2",
        tooltip: t("tooltips.kpi_oee"),
      },
      {
        key: "kpis.KIO",
        label: t("analysis.kpi_kio", "КИО %"),
        type: "pct",
        tooltip: t("tooltips.kpi_kio"),
      },
      {
        key: "kpis.KTG",
        label: t("analysis.kpi_ktg", "КТГ %"),
        type: "pct",
        tooltip: t("tooltips.kpi_ktg"),
      },
      {
        key: "kpis.KIV",
        label: t("analysis.kpi_kiv", "КИВ %"),
        type: "pct",
        tooltip: t("tooltips.kpi_kiv"),
      },
      {
        key: "kpis.KIN",
        label: t("analysis.kpi_kin", "КИН %"),
        type: "pct",
        tooltip: t("tooltips.kpi_kin"),
      },
      {
        key: "useful_time",
        label:
          t("analysis.useful_time_short", "Полезное время") + " (факт/всего)",
        type: "useful_time",
        tooltip: t("tooltips.useful_time"),
      },
      {
        key: "kpis.m3_per_hour",
        label: "Производительность (м³/ч)",
        type: "avg_float",
        tooltip: "Средняя выработка в кубометрах за 1 час чистой работы без простоев и обедов",
      },
      {
        key: "total_mileage_km",
        label: "Пробег по GPS (км)",
        type: "sum_float",
      },

      {
        key: "cycle_count_total",
        label: t("analysis.total_cycles", "Всего циклов"),
        type: "sum_number",
        tooltip: t("tooltips.total_cycles"),
      },
      {
        key: "cycle_count_vehicle",
        label: t("analysis.cycles_vehicle", "Циклов в машину"),
        type: "sum_number",
        tooltip: t("tooltips.cycles_vehicle"),
      },
      {
        key: "cycle_count_ground",
        label: t("analysis.cycles_ground", "Циклов в землю"),
        type: "sum_number",
        tooltip: t("tooltips.cycles_ground"),
      },

      {
        key: "estimated_volume_total",
        label:
          t("analysis.estimated_volume_total", "Ном. объем (всего)") + " м³",
        type: "sum_float",
        tooltip: t("tooltips.volume_total"),
      },
      {
        key: "estimated_volume_vehicle",
        label:
          t("analysis.estimated_volume_vehicle", "Ном. объем (в машину)") +
          " м³",
        type: "sum_float",
        tooltip: t("tooltips.volume_vehicle"),
      },
      {
        key: "estimated_volume_ground",
        label:
          t("analysis.estimated_volume_ground", "Ном. объем (в землю)") + " м³",
        type: "sum_float",
        tooltip: t("tooltips.volume_ground"),
      },

      {
        key: "estimated_mass_total",
        label: "Ном. масса (всего) т",
        type: "sum_mass_tons",
      },
      {
        key: "estimated_mass_vehicle",
        label: "Ном. масса (в машину) т",
        type: "sum_mass_tons",
      },
      {
        key: "estimated_mass_ground",
        label: "Ном. масса (в землю) т",
        type: "sum_mass_tons",
      },

      {
        key: "ai_volume_total",
        label:
          "AI " + t("camera_twin.total_vol", "Объем двойника (всего)") + " м³",
        type: "sum_float",
        isTwin: true,
        tooltip: t("tooltips.ai_volume_total"),
      },
      {
        key: "ai_mass_total",
        label: "AI Масса двойника (всего) т",
        type: "sum_mass_tons",
        isTwin: true,
      },

      {
        key: "truck_arrivals_total",
        label: t("analysis.truck_arrivals_total", "Общее кол-во подъездов"),
        type: "sum_number",
        tooltip: t("tooltips.truck_arrivals"),
      },
      {
        key: "averages.vehicle.loading_session_sec",
        label: t("analysis.avg_loading_session", "Ср. время загрузки машины"),
        type: "avg_time",
        tooltip: t("tooltips.avg_loading_session"),
      },

      {
        key: "averages.vehicle.total_sec",
        label:
          t("analysis.avg_cycle_total", "Ср. цикл") +
          " (" +
          t("analysis.in_vehicle", "В машину") +
          ")",
        type: "avg_sec",
        tooltip: t("tooltips.avg_cycle_vehicle"),
      },
      {
        key: "averages.vehicle.empty_sec",
        label: t("analysis.avg_phase_empty", "Возврат и копание"),
        type: "avg_sec",
      },
      {
        key: "averages.vehicle.full_sec",
        label: t("analysis.avg_phase_full", "Поворот на разгрузку"),
        type: "avg_sec",
      },
      {
        key: "averages.vehicle.unload_sec",
        label: t("analysis.avg_phase_unload", "Процесс разгрузки"),
        type: "avg_sec",
      },

      {
        key: "averages.vehicle.delay_sec",
        label: t("analysis.avg_phase_delay", "Орг. задержки (микро-простои)"),
        type: "avg_sec",
        tooltip: t("tooltips.avg_phase_delay", "Скрытые организационные потери"),
      },
      {
        key: "averages.ground.total_sec",
        label:
          t("analysis.avg_cycle_total", "Ср. цикл") +
          " (" +
          t("analysis.in_ground", "В землю") +
          ")",
        type: "avg_sec",
        tooltip: t("tooltips.avg_cycle_ground"),
      },

      {
        key: "durations_by_class_sec.actual_downtime",
        label: t("analysis.actual_downtime", "Время простоя техники"),
        type: "sum_time",
        tooltip: t("tooltips.actual_downtime"),
      },
      {
        key: "durations_by_class_sec.no_data",
        label: t("analysis.no_data_time", "Время (нет данных)"),
        type: "sum_time",
        tooltip: t("tooltips.no_data"),
      },
      {
        key: 'durations_by_class_sec.non_working_time',
        label: 'Нерабочее время',
        type: 'sum_time'
      },
      {
        key: 'illegal_work.duration_sec',
        label: 'Работа вне смены(время)',
        type: 'sum_time'
      },
      {
        key: 'illegal_work.volume',
        label: 'Работа вне смены (объем) м³',
        type: 'sum_float'
      },
    ],
    [t],
  );

  const handleMultiPresetChange = (preset) => {
    setMultiPreset(preset);
    if (preset === "custom") return;

    const now = dayjs();
    let start, end;

    switch (preset) {
      case "today":
        start = now.startOf("day").toDate();
        end = now.endOf("day").toDate();
        break;
      case "yesterday":
        start = now.subtract(1, "day").startOf("day").toDate();
        end = now.subtract(1, "day").endOf("day").toDate();
        break;
      case "last_2_days":
        start = now.subtract(1, "day").startOf("day").toDate();
        end = now.endOf("day").toDate();
        break;
      case "last_3_days":
        start = now.subtract(2, "day").startOf("day").toDate();
        end = now.endOf("day").toDate();
        break;
      case "last_7_days":
        start = now.subtract(6, "day").startOf("day").toDate();
        end = now.endOf("day").toDate();
        break;
      case "last_14_days":
        start = now.subtract(13, "day").startOf("day").toDate();
        end = now.endOf("day").toDate();
        break;
      case "last_month":
        start = now.subtract(1, "month").startOf("day").toDate();
        end = now.endOf("day").toDate();
        break;
      default:
        return;
    }
    setMultiStartDate(start);
    setMultiEndDate(end);
  };

  useEffect(() => {
    if (multiCooldown > 0) {
      const timer = setTimeout(() => setMultiCooldown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [multiCooldown]);

  useEffect(() => {
    fetchDevicesAndZones();
  }, []);

  useEffect(() => {
    setSelectedZoneFilter("");
    setSelectedTypeFilter("");
  }, [selectedOrgFilter]);

  const processedDevices = useMemo(() => {
    let result = devices.filter((dev) => {
      const query = searchQuery.toLowerCase();
      const nameMatch = dev.name.toLowerCase().includes(query);

      let orgMatchSearch = false;
      if (isSuperuser && dev.organization_name) {
        orgMatchSearch = dev.organization_name.toLowerCase().includes(query);
      }
      const matchesSearch = nameMatch || orgMatchSearch;

      let matchesOrgDropdown = true;
      if (isSuperuser && selectedOrgFilter) {
        matchesOrgDropdown = dev.organization_name === selectedOrgFilter;
      }

      let matchesZone = true;
      if (selectedZoneFilter !== "") {
        matchesZone = dev.work_zone === parseInt(selectedZoneFilter);
      }

      let matchesType = true;
      if (selectedTypeFilter !== "") {
        matchesType = dev.vehicle_type === parseInt(selectedTypeFilter);
      }

      return matchesSearch && matchesOrgDropdown && matchesZone && matchesType;
    });

    result.sort((a, b) => {
      if (sortBy === "name_asc") return a.name.localeCompare(b.name);
      if (sortBy === "name_desc") return b.name.localeCompare(a.name);
      if (sortBy === "last_seen_desc" || sortBy === "last_seen_asc") {
        const dateA = a.last_seen ? new Date(a.last_seen).getTime() : 0;
        const dateB = b.last_seen ? new Date(b.last_seen).getTime() : 0;
        return sortBy === "last_seen_desc" ? dateB - dateA : dateA - dateB;
      }
      return 0;
    });
    return result;
  }, [
    devices,
    searchQuery,
    sortBy,
    isSuperuser,
    selectedOrgFilter,
    selectedZoneFilter,
    selectedTypeFilter,
  ]);

  const toggleDeviceSelection = (id) =>
    setSelectedDevices((prev) =>
      prev.includes(id) ? prev.filter((devId) => devId !== id) : [...prev, id],
    );

  const handleSelectAll = () => {
    const allDisplayedSelected =
      processedDevices.length > 0 &&
      processedDevices.every((dev) => selectedDevices.includes(dev.id));
    if (allDisplayedSelected) {
      const displayedIds = processedDevices.map((dev) => dev.id);
      setSelectedDevices((prev) =>
        prev.filter((id) => !displayedIds.includes(id)),
      );
    } else {
      const displayedIds = processedDevices.map((dev) => dev.id);
      setSelectedDevices((prev) => {
        const newSelection = [...prev];
        displayedIds.forEach((id) => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const fetchMultiAnalytics = async () => {
    if (selectedDevices.length === 0) return;
    if (multiCooldown > 0) {
      toast.warn(`Подождите ${multiCooldown} сек.`);
      return;
    }
    setMultiLoading(true);
    setMultiData([]);
    const token = localStorage.getItem("token");
    const params = {
      start_date: multiStartDate.toISOString(),
      end_date: multiEndDate.toISOString(),
    };

    try {
      const promises = selectedDevices.map((id) => {
        const devObj = devices.find((d) => d.id === id);
        return axios
          .get("/api/analytics/", {
            headers: { Authorization: `Bearer ${token}` },
            params: { ...params, device_id: id },
          })
          .then((res) => ({ device: devObj, data: res.data, error: null }))
          .catch((err) => ({ device: devObj, data: null, error: err.message }));
      });
      const results = await Promise.all(promises);
      setMultiData(results);
    } catch (err) {
      toast.error("Ошибка при получении пакетной аналитики");
    } finally {
      setMultiLoading(false);
      if (!isSuperuser) setMultiCooldown(10);
    }
  };

  const getMetricValueLocal = (dataObj, key) => {
    if (!dataObj) return 0;
    const keys = key.split(".");
    let val = dataObj;
    for (const k of keys) {
      if (val === undefined || val === null) return 0;
      val = val[k];
    }
    return val || 0;
  };

  const handleExportMultiExcel = async () => {
    if (multiData.length === 0) {
      toast.warn("Сначала сформируйте отчет");
      return;
    }
    setExportingMultiExcel(true);

    const getKpiFill = (val) => {
      if (typeof val !== 'number') return null;
      if (val <= 40) return { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF66" } };
      if (val <= 60) return { type: "pattern", pattern: "solid", fgColor: { argb: "FF99FF99" } };
      return { type: "pattern", pattern: "solid", fgColor: { argb: "FFFF8080" } };
    };

    const getHeatmapFill = (val, min, max, reverse = false) => {
      if (typeof val !== 'number' || max === min || val == null || isNaN(val)) return null;
      let ratio = (val - min) / (max - min);
      if (reverse) ratio = 1 - ratio;

      let r, g, b;
      if (ratio < 0.5) {
        let norm = ratio * 2;
        r = 255; g = Math.round(128 + (127 * norm)); b = 128;
      } else {
        let norm = (ratio - 0.5) * 2;
        r = Math.round(255 - (102 * norm)); g = 255; b = Math.round(128 + (51 * norm));
      }
      const toHex = (c) => c.toString(16).padStart(2, '0').toUpperCase();
      return { type: "pattern", pattern: "solid", fgColor: { argb: `FF${toHex(r)}${toHex(g)}${toHex(b)}` } };
    };

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "System AI";
      const sheet = workbook.addWorksheet("Сводная аналитика", { views: [{ showGridLines: false }] });

      const cols = [
        { key: "param", width: 45 },
        { key: "fleet", width: 25 },
      ];
      multiData.forEach((res, i) => {
        cols.push({ key: `dev_${i}`, width: 25 });
      });
      sheet.columns = cols;

      const titleRow = sheet.addRow({ param: "=== ИНФОРМАЦИЯ ОБ ОТЧЕТЕ ===" });
      titleRow.font = { bold: true, size: 12 };
      sheet.addRow({
        param: "Период:",
        fleet: `${dayjs(multiStartDate).format("DD.MM.YYYY HH:mm")} - ${dayjs(multiEndDate).format("DD.MM.YYYY HH:mm")}`
      });

      const uniqueZones = [...new Set(multiData.map(r => r.device.work_zone_name).filter(Boolean))];
      sheet.addRow({ param: "Участок:", fleet: uniqueZones.length > 0 ? uniqueZones.join(", ") : "Все / Без привязки" });

      const deviceNames = multiData.map(r => r.device.name).join(", ");
      sheet.addRow({ param: "Техника:", fleet: deviceNames });

      sheet.addRow({});

      const headerDict = {
        param: t("dashboard.parameter", "Параметр"),
        fleet: "В СРЕДНЕМ ПО ПАРКУ"
      };
      multiData.forEach((res, i) => {
        headerDict[`dev_${i}`] = res.device.name;
      });

      const headerRow = sheet.addRow(headerDict);
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
      headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
      headerRow.alignment = { vertical: "middle", horizontal: "center" };
      headerRow.height = 30;

      const activeMetrics = COMPARISON_METRICS.filter((m) => selectedMetrics.includes(m.key));
      const rowsData = [];

      activeMetrics.forEach((metric) => {
        if (metric.isTwin && !multiData.some((res) => res.data?.use_digital_twin)) return;

        const rowData = { param: metric.label, isKpi: metric.type === "pct", isHeatmap: metric.type !== "pct", rawValues: [] };
        const isReverseHeatmap = metric.key.includes("downtime") || metric.key.includes("no_data") || metric.type.includes("avg_time") || metric.type.includes("avg_sec");
        rowData.reverse = isReverseHeatmap;

        let fleetSum = 0;
        let fleetCount = 0;
        let fleetTWork = 0;
        let fleetTUptime = 0;

        multiData.forEach((res) => {
          if (res.data && !res.error) {
            if (metric.type === "useful_time") {
              fleetTWork += res.data.kpis?.tWork || 0;
              const sched = res.data.total_duration_sec || 0;
              const noData = res.data.durations_by_class_sec?.no_data || 0;
              fleetTUptime += Math.max(0, sched - noData);
            } else {
              const val = getMetricValueLocal(res.data, metric.key);
              if (val > 0 || metric.type.includes("sum")) {
                fleetSum += val;
                fleetCount += 1;
              }
            }
          }
        });

        if (metric.type === "useful_time") {
          const uMh = fleetTWork / 3600.0;
          const tMh = fleetTUptime / 3600.0;
          const pct = tMh > 0 ? (uMh / tMh) * 100 : 0;

          const devWork = [], devUptime = [], devPct = [];

          multiData.forEach((res) => {
            if (res.error || !res.data) {
              devWork.push({ text: "Ошибка / Нет данных", num: null });
              devUptime.push({ text: "Ошибка / Нет данных", num: null });
              devPct.push({ text: "Ошибка / Нет данных", num: null });
            } else {
              const duMh = (res.data.kpis?.tWork || 0) / 3600.0;
              const sched = res.data.total_duration_sec || 0;
              const noData = res.data.durations_by_class_sec?.no_data || 0;
              const dtMh = Math.max(0, sched - noData) / 3600.0;
              const dpct = dtMh > 0 ? (duMh / dtMh) * 100 : 0;
              devWork.push({ text: `${duMh.toFixed(1)} маш.-ч`, num: duMh });
              devUptime.push({ text: `${dtMh.toFixed(1)} маш.-ч`, num: dtMh });
              devPct.push({ text: `${dpct.toFixed(1)}%`, num: dpct });
            }
          });

          rowsData.push({ param: "Полезное время (факт)", fleet: `${uMh.toFixed(1)} маш.-ч`, rawValues: devWork, isHeatmap: true, reverse: false });
          rowsData.push({ param: "Полезное время (всего)", fleet: `${tMh.toFixed(1)} маш.-ч`, rawValues: devUptime, isHeatmap: true, reverse: false });
          rowsData.push({ param: "Полезное время (%)", fleet: `${pct.toFixed(1)}%`, rawValues: devPct, isKpi: true });

        } else {
          let fleetDisplayVal = 0;
          if (metric.key === "durations_by_class_sec.actual_downtime") {
            let totalDowntimeAndNoData = fleetSum;
            multiData.forEach((res) => {
              if (res.data && !res.error) totalDowntimeAndNoData += getMetricValueLocal(res.data, "durations_by_class_sec.no_data");
            });
            rowData.fleet = formatMetric(totalDowntimeAndNoData, metric.type);
          } else {
            fleetDisplayVal = metric.type.includes("avg") && fleetCount > 0 ? fleetSum / fleetCount : fleetSum;
            rowData.fleet = formatMetric(fleetDisplayVal, metric.type);
          }

          multiData.forEach((res) => {
            if (res.error || !res.data) {
              rowData.rawValues.push({ text: "Ошибка / Нет данных", num: null });
            } else {
              const val = getMetricValueLocal(res.data, metric.key);
              if (metric.key === "durations_by_class_sec.actual_downtime") {
                const noDataVal = getMetricValueLocal(res.data, "durations_by_class_sec.no_data");
                rowData.rawValues.push({ text: formatMetric(val + noDataVal, metric.type), num: val + noDataVal });
              } else {
                rowData.rawValues.push({ text: formatMetric(val, metric.type), num: val });
              }
            }
          });
          rowsData.push(rowData);
        }
      });

      const allShiftNames = [...new Set(multiData.filter((r) => !r.error && r.data?.shift_stats).flatMap((r) => Object.keys(r.data.shift_stats)))];
      if (allShiftNames.length > 0) {
        rowsData.push({ isSpacer: true });
        rowsData.push({ param: "=== ДЕТАЛИЗАЦИЯ ПО СМЕНАМ ===", isHeader: true });

        allShiftNames.forEach((shiftName) => {
          rowsData.push({ param: `Смена: ${shiftName}`, isSubHeader: true });

          const params = [
            { key: "cycles_tot", label: "  Циклы (всего)", type: "num" },
            { key: "cycles_veh", label: "  Циклы (авто)", type: "num" },
            { key: "cycles_gnd", label: "  Циклов (земля)", type: "num" },
            { key: "volume_veh", label: "  Объем (авто) м³", type: "float" },
            { key: "volume_gnd", label: "  Объем (земля) м³", type: "float" },
            { key: "mass_veh", label: "  Масса (авто) т", type: "mass" },
            { key: "mass_gnd", label: "  Масса (земля) т", type: "mass" },
            { key: "downtime", label: "  Отсутствие видимости", type: "time", reverse: true },
          ];

          params.forEach((p) => {
            const rData = { param: p.label, fleet: "-", rawValues: [], isHeatmap: true, reverse: p.reverse };
            multiData.forEach((res) => {
              if (res.error || !res.data?.shift_stats?.[shiftName]) {
                rData.rawValues.push({ text: "-", num: null });
              } else {
                const s = res.data.shift_stats[shiftName];
                let val = 0;
                if (p.key === "cycles_tot") val = s.cycles_total || 0;
                if (p.key === "cycles_veh") val = s.cycles_vehicle || 0;
                if (p.key === "cycles_gnd") val = s.cycles_ground || 0;
                if (p.key === "volume_veh") val = s.volume_vehicle || 0;
                if (p.key === "volume_gnd") val = s.volume_ground || 0;
                if (p.key === "mass_veh") val = s.mass_vehicle || 0;
                if (p.key === "mass_gnd") val = s.mass_ground || 0;
                if (p.key === "downtime") val = (s.actual_downtime || 0) + (s.no_data || 0);

                let text = val;
                if (p.type === "float") text = Number(val).toFixed(1);
                if (p.type === "mass") { val = val / 1000; text = val.toFixed(1); }
                if (p.type === "time") text = formatDuration(val);
                rData.rawValues.push({ text: text.toString(), num: val });
              }
            });
            rowsData.push(rData);
          });
        });
      }

      const fleetTruckStats = {};
      multiData.forEach((res) => {
        if (res.data && res.data.truck_stats_summary) {
          Object.entries(res.data.truck_stats_summary).forEach(([truckName, tStat]) => {
            if (!fleetTruckStats[truckName]) { fleetTruckStats[truckName] = { arrivals: 0, cycles: 0, vol: 0, mass: 0 }; }
            fleetTruckStats[truckName].arrivals += tStat.arrivals || 0;
            fleetTruckStats[truckName].cycles += tStat.cycles || 0;
            fleetTruckStats[truckName].vol += Number(tStat.vol || 0);
            fleetTruckStats[truckName].mass += Number(tStat.mass || 0);
          });
        }
      });

      const allTruckNames = Object.keys(fleetTruckStats);
      if (allTruckNames.length > 0) {
        rowsData.push({ isSpacer: true });
        rowsData.push({ param: "=== СТАТИСТИКА ПО САМОСВАЛАМ ===", fleet: "СУММА ПО ПАРКУ", isHeader: true });

        allTruckNames.forEach((truckName) => {
          rowsData.push({ param: `Самосвал: ${truckName}`, isSubHeader: true });

          const params = [
            { key: "arrivals", label: "  Подъездов", type: "num" },
            { key: "cycles", label: "  Всего ковшей", type: "num" },
            { key: "volume", label: "  Объем породы (м³)", type: "float" },
            { key: "mass", label: "  Масса породы (т)", type: "mass" },
          ];

          params.forEach((p) => {
            const fleetVal = fleetTruckStats[truckName];
            let fleetDisplay = "";
            let fNum = 0;
            if (p.key === "arrivals") fNum = fleetVal.arrivals;
            if (p.key === "cycles") fNum = fleetVal.cycles;
            if (p.key === "volume") fNum = fleetVal.vol;
            if (p.key === "mass") fNum = fleetVal.mass / 1000;
            fleetDisplay = p.type === "num" ? fNum : Number(fNum).toFixed(1);

            const rData = { param: p.label, fleet: fleetDisplay, rawValues: [], isHeatmap: true, reverse: false };

            multiData.forEach((res) => {
              if (res.error || !res.data?.truck_stats_summary?.[truckName]) {
                rData.rawValues.push({ text: "-", num: null });
              } else {
                const tStat = res.data.truck_stats_summary[truckName];
                let val = 0;
                if (p.key === "arrivals") val = tStat.arrivals;
                if (p.key === "cycles") val = tStat.cycles;
                if (p.key === "volume") val = tStat.vol;
                if (p.key === "mass") val = tStat.mass / 1000;

                let text = p.type === "num" ? val : Number(val).toFixed(1);
                rData.rawValues.push({ text: text.toString(), num: val });
              }
            });
            rowsData.push(rData);
          });
        });
      }

      rowsData.forEach(r => {
        if (r.isSpacer) {
          sheet.addRow([]).height = 10;
          return;
        }

        const rowDict = { param: r.param, fleet: r.fleet || "" };
        if (r.rawValues) {
          r.rawValues.forEach((d, i) => { rowDict[`dev_${i}`] = d.text; });
        }

        const xlRow = sheet.addRow(rowDict);
        xlRow.height = r.isHeader ? 25 : 20;

        xlRow.eachCell((cell, colNum) => {
          cell.alignment = { vertical: "middle", horizontal: colNum === 1 ? "left" : "center" };
          if (!r.isHeader && !r.isSubHeader) {
            cell.border = { bottom: { style: "thin", color: { argb: "FFCCCCCC" } } };
          }
        });

        if (r.isHeader) {
          xlRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
          xlRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF5A5A5A" } };
          return;
        }

        if (r.isSubHeader) {
          xlRow.font = { bold: true, color: { argb: "FF009900" } };
          xlRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
          return;
        }

        xlRow.getCell(2).font = { bold: true, color: { argb: "FF0000FF" } };

        let minV = Infinity, maxV = -Infinity;
        if (r.isHeatmap && r.rawValues) {
          r.rawValues.forEach(d => {
            if (d.num != null && !isNaN(d.num)) {
              if (d.num < minV) minV = d.num;
              if (d.num > maxV) maxV = d.num;
            }
          });
        }

        if (r.rawValues) {
          r.rawValues.forEach((d, i) => {
            const cell = xlRow.getCell(i + 3);
            if (d.num != null && !isNaN(d.num)) {
              if (r.isKpi) {
                const fill = getKpiFill(d.num);
                if (fill) cell.fill = fill;
              } else if (r.isHeatmap) {
                const fill = getHeatmapFill(d.num, minV, maxV, r.reverse);
                if (fill) cell.fill = fill;
              }
            }
          });
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const filename = `Multi_Analytics_${dayjs().format("YYYY-MM-DD")}.xlsx`;
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, filename);
      toast.success(t("common.success"));
    } catch (error) {
      console.error(error);
      toast.error(t("common.error"));
    } finally {
      setExportingMultiExcel(false);
    }
  };

  const handleEditClick = (device) => {
    if (!isStaff) {
      toast.warn(t("common.role_restricted"));
      return;
    }
    setEditingDevice({ ...device });
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingDevice(null);
  };

  const handleInputChange = (e) => {
    setEditingDevice((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    const volume = parseFloat(editingDevice.bucket_volume);
    const geomVolume = parseFloat(editingDevice.geometric_bucket_volume);
    if (isNaN(volume) || volume <= 0 || isNaN(geomVolume) || geomVolume <= 0) {
      toast.warn("Объемы должны быть > 0");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const dataToSave = {
        name: editingDevice.name,
        bucket_volume: volume,
        geometric_bucket_volume: geomVolume,
        omnicomm_id: editingDevice.omnicomm_id ? parseInt(editingDevice.omnicomm_id) : null,
        equipment_spec: editingDevice.equipment_spec ? parseInt(editingDevice.equipment_spec) : null,
        fuel_k_factor: parseFloat(editingDevice.fuel_k_factor) || 1.0,
        work_zone: editingDevice.work_zone
          ? parseInt(editingDevice.work_zone)
          : null,
        vehicle_type: editingDevice.vehicle_type ? parseInt(editingDevice.vehicle_type) : null,
      };
      await axios.patch(`/api/devices/${editingDevice.id}/`, dataToSave, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const selectedZone = workZones.find((z) => z.id === dataToSave.work_zone);
      const zoneName = selectedZone ? selectedZone.name : null;

      setDevices(
        devices.map((d) =>
          d.id === editingDevice.id
            ? { ...d, ...dataToSave, work_zone_name: zoneName }
            : d,
        ),
      );

      toast.success(t("common.success"));
      handleModalClose();
    } catch (error) {
      toast.error(t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const handleAddClick = () => {
    if (!isStaff) {
      toast.warn(t("common.role_restricted"));
      return;
    }
    setNewDevice({ name: "", bucket_volume: 1.2, geometric_bucket_volume: 1.0 });
    setCreateModalVisible(true);
  };

  const handleNewDeviceChange = (e) => {
    const { name, value } = e.target;
    setNewDevice((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async () => {
    if (!newDevice.name.trim()) {
      toast.warn("Введите название");
      return;
    }
    setCreating(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        ...newDevice,
        bucket_volume: parseFloat(newDevice.bucket_volume) || 1.2,
        geometric_bucket_volume: parseFloat(newDevice.geometric_bucket_volume) || 1.0,
        work_zone: newDevice.work_zone ? parseInt(newDevice.work_zone) : null,
        equipment_spec: newDevice.equipment_spec ? parseInt(newDevice.equipment_spec) : null,
        fuel_k_factor: parseFloat(newDevice.fuel_k_factor) || 1.0,
        vehicle_type: newDevice.vehicle_type ? parseInt(newDevice.vehicle_type) : null,
      };
      await axios.post("/api/devices/", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(t("common.success"));
      setCreateModalVisible(false);
      fetchDevicesAndZones();
    } catch (error) {
      toast.error(t("common.error"));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = (device) => {
    if (!isStaff) {
      toast.warn(t("common.role_restricted"));
      return;
    }
    setDeviceToDelete(device);
    setDeleteConfirmationWord("");
    setDeleteModalVisible(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/devices/${deviceToDelete.id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDevices(devices.filter((d) => d.id !== deviceToDelete.id));
      setSelectedDevices((prev) =>
        prev.filter((id) => id !== deviceToDelete.id),
      );
      toast.success(t("common.success"));
      setDeleteModalVisible(false);
    } catch (error) {
      toast.error(t("common.error"));
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("is_staff");
    localStorage.removeItem("is_superuser");
    localStorage.removeItem("can_do_markup");
    localStorage.removeItem("organizationName");
    localStorage.removeItem("organizationId");
    localStorage.removeItem("dashboardOrgFilter");
    window.location.href = "/login";
  };

  const handleViewPassport = async (deviceId, deviceName) => {
    setPassportModal({ visible: true, url: "", filename: "", loading: true });
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/devices/${deviceId}/generate-passport/`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const disposition = res.headers['content-disposition'];
      const ext = disposition && disposition.includes('.docx') ? 'docx' : 'pdf';
      const filename = `Тех_Паспорт_${deviceName}.${ext}`;

      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);

      setPassportModal({ visible: true, url, filename, loading: false });
    } catch (err) {
      toast.error("Ошибка формирования паспорта");
      setPassportModal({ visible: false, url: "", filename: "", loading: false });
    }
  };

  const filteredWorkZones = useMemo(() => {
    if (!isSuperuser) return workZones;
    if (!selectedOrgFilter) return workZones;
    return workZones.filter((z) => z.organization_name === selectedOrgFilter);
  }, [workZones, isSuperuser, selectedOrgFilter]);

  const getDeviceStatus = (device) => {
    if (!device.is_active)
      return { color: "secondary", text: t("dashboard.disabled", "Отключен") };
    if (!device.last_seen)
      return { color: "dark", text: t("dashboard.offline", "Оффлайн") };
    const diffMinutes = dayjs().diff(dayjs(device.last_seen), "minute");
    return diffMinutes < 120
      ? { color: "success", text: t("dashboard.online", "Онлайн") }
      : { color: "danger", text: t("dashboard.offline", "Оффлайн") };
  };

  if (loading)
    return (
      <div className="text-center p-5">
        <CSpinner />
      </div>
    );

  const hasOmnicommConfig = devices.some((d) => d.has_omnicomm_config);

  return (
    <div className="bg-light min-vh-100 p-2 p-md-4">
      <CContainer fluid className="dashboard-centered mobile-p-small">
        <DashboardHeader
          t={t}
          organizationName={organizationName}
          selectedDevices={selectedDevices}
          processedDevices={processedDevices}
          isStaff={isStaff}
          canDoMarkup={canDoMarkup}
          isSuperuser={isSuperuser}
          setMultiModalVisible={setMultiModalVisible}
          setDisconnectModalVisible={setDisconnectModalVisible}
          handleSelectAll={handleSelectAll}
          handleAddClick={handleAddClick}
          handleLogout={handleLogout}
          hasOmnicommConfig={hasOmnicommConfig}
        />

        {/* ПЛАШКА: ПРЕДУПРЕЖДЕНИЕ ОБ ИСТЕКАЮЩЕМ СРОКЕ СЛУЖБЫ ОБОРУДОВАНИЯ */}
        {expiringDevices.length > 0 && (
          <div className="alert alert-warning border-warning shadow-sm mb-4 d-flex align-items-center justify-content-between p-3 rounded-3">
            <div className="d-flex align-items-center">
              <span className="fs-2 me-3">⚠️</span>
              <div>
                <h6 className="fw-bold text-dark mb-1">
                  Внимание! Истекает 24-месячный срок службы оборудования:
                </h6>
                <div className="small text-dark mt-1">
                  {expiringDevices.map((d) => (
                    <span key={d.id} className="me-3 d-inline-block">
                      • <strong>«{d.name}»</strong> — до{" "}
                      <strong>{dayjs(d.warranty_end_date).format("DD.MM.YYYY")}</strong>{" "}
                      ({d.warranty_days_left > 0
                        ? `осталось ${d.warranty_days_left} дн.`
                        : "срок истёк"})
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <FilterControls
          t={t}
          isSuperuser={isSuperuser}
          filteredWorkZones={filteredWorkZones}
          selectedZoneFilter={selectedZoneFilter}
          setSelectedZoneFilter={setSelectedZoneFilter}
          vehicleTypes={vehicleTypes}
          selectedTypeFilter={selectedTypeFilter}
          setSelectedTypeFilter={setSelectedTypeFilter}
          devices={devices}
          selectedOrgFilter={selectedOrgFilter}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setSelectedOrgFilter={setSelectedOrgFilter}
          uniqueOrganizations={uniqueOrganizations}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />

        <DeviceGrid
          t={t}
          devices={devices}
          processedDevices={processedDevices}
          selectedDevices={selectedDevices}
          toggleDeviceSelection={toggleDeviceSelection}
          getDeviceStatus={getDeviceStatus}
          isStaff={isStaff}
          handleEditClick={handleEditClick}
          handleDeleteClick={handleDeleteClick}
          handleViewPassport={handleViewPassport}
        />
      </CContainer>

      <SummaryAnalyticsModal
        visible={multiModalVisible}
        isSuperuser={isSuperuser}
        onClose={() => setMultiModalVisible(false)}
        t={t}
        currentLang={currentLang}
        multiPreset={multiPreset}
        handleMultiPresetChange={handleMultiPresetChange}
        multiStartDate={multiStartDate}
        setMultiStartDate={setMultiStartDate}
        multiEndDate={multiEndDate}
        setMultiEndDate={setMultiEndDate}
        multiLoading={multiLoading}
        fetchMultiAnalytics={fetchMultiAnalytics}
        multiData={multiData}
        showMetricsFilter={showMetricsFilter}
        setShowMetricsFilter={setShowMetricsFilter}
        selectedMetrics={selectedMetrics}
        setSelectedMetrics={setSelectedMetrics}
        COMPARISON_METRICS={COMPARISON_METRICS}
        classIdToMeta={classIdToMeta}
        exportingMultiExcel={exportingMultiExcel}
        handleExportMultiExcel={handleExportMultiExcel}
        multiCooldown={multiCooldown}
      />
      <DisconnectionsModal
        visible={disconnectModalVisible}
        onClose={() => setDisconnectModalVisible(false)}
        devices={devices}
        selectedDevices={selectedDevices}
      />
      <DeviceModals
        t={t}
        workZones={workZones}
        modalVisible={modalVisible}
        handleModalClose={handleModalClose}
        editingDevice={editingDevice}
        setEditingDevice={setEditingDevice}
        handleInputChange={handleInputChange}
        saving={saving}
        handleSave={handleSave}
        createModalVisible={createModalVisible}
        setCreateModalVisible={setCreateModalVisible}
        newDevice={newDevice}
        setNewDevice={setNewDevice}
        handleNewDeviceChange={handleNewDeviceChange}
        creating={creating}
        handleCreate={handleCreate}
        deleteModalVisible={deleteModalVisible}
        setDeleteModalVisible={setDeleteModalVisible}
        deviceToDelete={deviceToDelete}
        deleteConfirmationWord={deleteConfirmationWord}
        setDeleteConfirmationWord={setDeleteConfirmationWord}
        deleting={deleting}
        handleDelete={handleDelete}
        passportModal={passportModal}
        closePassportModal={closePassportModal}
        equipmentSpecs={equipmentSpecs}
        vehicleTypes={vehicleTypes}
      />

      {/* МОДАЛЬНОЕ ОКНО ПРИ ВХОДЕ В ЛИЧНЫЙ КАБИНЕТ (ПОЯВЛЯЕТСЯ РАЗ ЗА СЕССИЮ) */}
      <CModal
        visible={expiringDevices.length > 0 && !expiringModalDismissed}
        onClose={() => {
          sessionStorage.setItem("expiring_modal_dismissed", "true");
          setExpiringModalDismissed(true);
        }}
        alignment="center"
      >
        <CModalHeader className="bg-warning text-dark border-bottom-0">
          <CModalTitle className="fw-bold">
            ⚠️ Истекает срок службы оборудования
          </CModalTitle>
        </CModalHeader>
        <CModalBody className="p-4">
          <p className="mb-2">
            Внимание! Для следующего оборудования подходит к концу установленный 24-месячный срок службы:
          </p>
          <ul className="list-group mb-3 shadow-sm">
            {expiringDevices.map((dev) => (
              <li
                key={dev.id}
                className="list-group-item d-flex justify-content-between align-items-center"
              >
                <strong>{dev.name}</strong>
                <span
                  className={`badge ${dev.warranty_days_left <= 7
                      ? "bg-danger"
                      : "bg-warning text-dark"
                    }`}
                >
                  до {dayjs(dev.warranty_end_date).format("DD.MM.YYYY")} (
                  {dev.warranty_days_left > 0
                    ? `осталось ${dev.warranty_days_left} дн.`
                    : "срок истёк"}
                  )
                </span>
              </li>
            ))}
          </ul>
          <p className="text-muted small mb-0">
            Пожалуйста, свяжитесь со специалистами сервисной службы или технической поддержки Neimarker для проведения регламентного технического обслуживания или продления договора сервиса.
          </p>
        </CModalBody>
        <CModalFooter className="bg-light">
          <CButton
            color="primary"
            className="fw-bold px-4"
            onClick={() => {
              sessionStorage.setItem("expiring_modal_dismissed", "true");
              setExpiringModalDismissed(true);
            }}
          >
            Понятно
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
};

export default DashboardPage;